<template>
  <base-card>
    <base-card-body>
      <div class="flex flex-col gap-1">
        <div class="flex justify-between">
          <div class="flex flex-col">
            <label class="font-bold mb-2">{{ healthcenter.name }} ({{ healthcenter.cip }})</label>
            <label v-if="healthcenter.holder?.name">
              {{ healthcenter.holder.name }}
            </label>
            <label
              v-if="healthcenter.holder"
              class="text-sm"
            >{{ healthcenter.holder?.email }}</label>
          </div>
          <div v-if="canDeleteMember && !healthcenterIsGroupOwner">
            <button @click="confirmDelete">
              <icon
                name="mdi-delete"
                color="red"
                :size="25"
              />
            </button>
          </div>
          <div v-if="canLeave && !healthcenterIsGroupOwner && !groupingGroup">
            <button @click="confirmLeave">
              <v-icon
                color="red"
                :size="25"
              >
                mdi-exit-to-app
              </v-icon>
            </button>
          </div>
        </div>
        <div class="flex flex-row justify-between text-sm mt-2">
          <label>
            {{ address ?? '' }}
          </label>
          <label
            v-if="healthcenter.last_lgo_import"
            class="self-end text-gray-500"
          >
            Dernière transmission: {{ $filters.date(healthcenter.last_lgo_import, 'DD/MM/YYYY') }}
          </label>
        </div>
      </div>
    </base-card-body>
  </base-card>
</template>

<script setup lang="ts">
import {computed, inject} from 'vue';
import {HealthcenterGroupMember} from "@/disruption-logistics/interfaces/healthcenter-groups";
import HealthcenterGroupService from "@/disruption-logistics/services/HealthcenterGroupService";
import {disruptionLogisticsApi} from "@/container";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import Icon from "@/core/components/icon/Icon.vue";
import {useRouter} from "vue-router";

const props = defineProps<{
    healthcenter: HealthcenterGroupMember,
    ownerAccessId: number,
    groupId: number | null,
    groupingGroup: boolean
}>();
const emits = defineEmits(['member-deleted']);
const router = useRouter();

const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => never | Promise<void>
) => Promise<void>;

/**
 * Get the healthcenter address.
 */
const address = computed((): string | null => {
    if (props.healthcenter.address) {
        return props.healthcenter.address.formatted;
    }

    return null;
});

/**
 * Check if the current user can delete the member.
 */
const canDeleteMember = computed(() => {
    return HealthcenterGroupService.isOwner(props.ownerAccessId);
});

/**
 * Determine if the user can leave the group.
 */
const canLeave = computed(() => {
    return HealthcenterGroupService.healthcenterIsAuth(props.healthcenter.id);
});

/**
 * Check if the healthcenter is the owner of the group.
 */
const healthcenterIsGroupOwner = computed((): boolean => {
    return props.healthcenter.owners?.some(owner => owner.id === props.ownerAccessId) ?? false;
});

/**
 * Delete the member, then emit the member-deleted event.
 */
const deleteMember = async () => {
    if (!props.groupId) {
        return Promise.resolve();
    }

    return disruptionLogisticsApi()
        .deleteHealthcenterGroupMember(props.groupId, props.healthcenter.id)
        .then(() => {
            emits('member-deleted', props.healthcenter.id);
        });
};

/**
 * Leave the group.
 */
const leaveGroup = async () => {
    if (!props.groupId) {
        return Promise.resolve();
    }

    return disruptionLogisticsApi()
        .leaveHealthcenterGroup(props.groupId)
        .then(() => {
            router.replace({name: 'disruption-logistics.healthcenter-groups'});
        });
};


/**
 * Confirm the deletion of the member.
 */
const confirmDelete = () => {
    openConfirmationModal(
        'Suppression d\'un membre',
        'Êtes-vous sûr de vouloir supprimer ce membre ?',
        deleteMember
    );
};

/**
 * Confirm leaving the group.
 */
const confirmLeave = () => {
    openConfirmationModal(
        'Quitter le groupe',
        'En quittant le groupe, vous ne pourrez plus accéder aux données partagées par les membres du groupe, et vos données ne seront plus accessibles par les membres du groupe.',
        leaveGroup
    );
};
</script>

<style scoped>

</style>
